import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { MessageCircle, X } from "lucide-react";
import { waLink } from "../data/store";

const CONSULT_MESSAGE =
  "Halo NJA, saya ingin konsultasi kebutuhan bahan bangunan. Mohon bantuannya.";
const STORAGE_KEY = "nja-wa-bubble-dismissed";

export function WhatsAppWidget() {
  const [panelOpen, setPanelOpen] = useState(false);
  const [bubbleVisible, setBubbleVisible] = useState(false);
  const dismissedRef = useRef(false);
  const buttonRef = useRef(null);
  const closeRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const waUrl = waLink(CONSULT_MESSAGE);

  useEffect(() => {
    try {
      dismissedRef.current = sessionStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      dismissedRef.current = false;
    }
    if (dismissedRef.current) return;
    const t = setTimeout(() => setBubbleVisible(true), 5000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!panelOpen) return;
    closeRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") {
        setPanelOpen(false);
        buttonRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [panelOpen]);

  const dismissBubble = () => {
    setBubbleVisible(false);
    dismissedRef.current = true;
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch {}
  };

  const togglePanel = () => {
    setPanelOpen((open) => {
      if (!open) setBubbleVisible(false);
      return !open;
    });
  };

  const anim = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 12 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: 8 },
        transition: { duration: 0.25 },
      };

  return (
    <div className="wa-widget-safe fixed right-4 z-50 sm:right-6">
      <AnimatePresence>
        {panelOpen && (
          <motion.div
            {...anim}
            role="dialog"
            aria-label="Panel bantuan WhatsApp"
            data-testid="wa-panel"
            className="absolute bottom-[72px] right-0 w-80 max-w-[calc(100vw-32px)] rounded-2xl border border-slate-200 bg-white p-5 shadow-xl"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-base font-bold text-navy">Butuh bantuan?</p>
                <p className="text-sm text-slate-500">Tim Nuansa Jaya Abadi</p>
              </div>
              <button
                ref={closeRef}
                type="button"
                onClick={() => {
                  setPanelOpen(false);
                  buttonRef.current?.focus();
                }}
                aria-label="Tutup panel bantuan"
                data-testid="wa-panel-close"
                className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-navy"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Halo 👋 Sedang mencari bahan bangunan? Hubungi kami untuk bertanya
              tentang pilihan barang, harga, dan ketersediaan.
            </p>
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="wa-panel-cta"
              className="mt-4 flex items-center justify-center gap-2 rounded-full bg-wa px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-wa-dark"
            >
              <MessageCircle className="h-5 w-5" aria-hidden="true" />
              Hubungi untuk Konsultasi
            </a>
            <p className="mt-3 text-center text-xs text-slate-400">
              Percakapan dilanjutkan melalui WhatsApp.
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex items-end justify-end gap-3">
        <AnimatePresence>
          {bubbleVisible && !panelOpen && (
            <motion.div
              {...anim}
              data-testid="wa-bubble"
              className="mb-2 flex items-center gap-2 rounded-2xl border border-slate-200 bg-white py-2.5 pl-4 pr-2 shadow-lg"
            >
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="wa-bubble-link"
                className="text-sm font-semibold text-navy"
              >
                Hubungi untuk Konsultasi
              </a>
              <button
                type="button"
                onClick={dismissBubble}
                aria-label="Tutup balon ajakan"
                data-testid="wa-bubble-close"
                className="rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-navy"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
        <button
          ref={buttonRef}
          type="button"
          onClick={togglePanel}
          onMouseEnter={() => {
            if (!panelOpen) setBubbleVisible(true);
          }}
          onFocus={() => {
            if (!panelOpen) setBubbleVisible(true);
          }}
          onMouseLeave={() => {
            if (dismissedRef.current) setBubbleVisible(false);
          }}
          aria-label="Buka panel bantuan WhatsApp"
          aria-expanded={panelOpen}
          data-testid="wa-widget-button"
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-wa text-white shadow-lg transition-transform hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy"
        >
          <MessageCircle className="h-7 w-7" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
